/**
 * Stale-while-revalidate persistence for read-only reference data.
 *
 * Repeat visits used to re-fetch the same slow-changing lists (countries,
 * cities, pricing reference, storefront themes) before anything could render.
 * We snapshot those specific caches into localStorage and hydrate them
 * synchronously on boot, so the UI paints from cache immediately while React
 * Query revalidates in the background.
 *
 * Deliberately allow-listed: nothing user-scoped, nothing sensitive, nothing
 * transactional (bookings, offers, messages, auth) is ever written to disk.
 */
import type { QueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "ec_query_cache_v1";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h — revalidated on every boot anyway
const MAX_BYTES = 512 * 1024;

/** Only these query-key roots are ever persisted. */
const PERSISTABLE = new Set([
  // Actual query keys used by the reference-data hooks. These are slow-changing
  // public lists; every entry here is revalidated in the background on boot.
  "locations",
  "dataset-countries",
  "catalog-items",
  "route-covered-cities",
  // Legacy / future reference keys — harmless if unused.
  "countries",
  "cities",
  "operation-locations",
  "public-pricing",
  "storefront-themes",
  "seo-routes",
  "platform-settings",
]);

type Snapshot = { t: number; e: [string, unknown][] };

const isPersistable = (key: unknown): boolean =>
  Array.isArray(key) && typeof key[0] === "string" && PERSISTABLE.has(key[0]);

export function hydrateQueryCache(client: QueryClient) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const snap = JSON.parse(raw) as Snapshot;
    if (!snap?.t || Date.now() - snap.t > MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    for (const [k, data] of snap.e) {
      const key = JSON.parse(k) as unknown[];
      if (!isPersistable(key)) continue;
      if (client.getQueryData(key) === undefined) client.setQueryData(key, data);
    }
  } catch {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }
}

export function startQueryPersistence(client: QueryClient) {
  let timer: number | undefined;

  const flush = () => {
    try {
      const entries: [string, unknown][] = [];
      for (const q of client.getQueryCache().getAll()) {
        if (!isPersistable(q.queryKey)) continue;
        if (q.state.status !== "success" || q.state.data === undefined) continue;
        entries.push([JSON.stringify(q.queryKey), q.state.data]);
      }
      if (!entries.length) return;
      const payload = JSON.stringify({ t: Date.now(), e: entries } satisfies Snapshot);
      if (payload.length > MAX_BYTES) return;
      localStorage.setItem(STORAGE_KEY, payload);
    } catch {
      /* quota or serialization failure — caching is best-effort */
    }
  };

  const schedule = () => {
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(flush, 1500);
  };

  const unsubscribe = client.getQueryCache().subscribe((event) => {
    if (event.type === "updated" && isPersistable(event.query.queryKey)) schedule();
  });

  window.addEventListener("pagehide", flush);
  return () => {
    unsubscribe();
    window.removeEventListener("pagehide", flush);
    if (timer) window.clearTimeout(timer);
  };
}
